/**
 * The converter for `znms-roi-scenario` v1 — the format the page this app
 * replaces exports.
 *
 * Everyone who has been modelling ZNMS has a folder of those files, and telling
 * them to retype a scenario is how a replacement gets refused. The mapping is
 * the one in docs/03-domain-model.md#the-znms-page-expressed-in-this-model:
 * three fixed packages become three packages, five fixed cost knobs become five
 * line items, and the page's 2-year commitment becomes an explicit field.
 *
 * The result is handed to {@link normalizeScenarioModel}, so a hand-edited or
 * truncated legacy file lands in the same clamped, self-consistent state as
 * anything else.
 */

import { LEGACY_EXPORT_SCHEMA } from "../constants.js";
import { normalizeScenarioModel } from "./normalize.js";
import type { LineBasis, LineItemInput, ScenarioModel } from "./types.js";

/** The page's three package keys, in the order it listed them. */
const LEGACY_PACKAGES = [
  { key: "starter", name: "Starter" },
  { key: "pro", name: "Pro" },
  { key: "enterprise", name: "Enterprise" },
] as const;

/** `AddonBasis` → the line-item basis that means the same thing. */
const LEGACY_ADDON_BASIS: Record<string, LineBasis> = {
  perClientYear: "PER_ACTIVE_CLIENT_YEAR",
  perClientOnce: "PER_NEW_CLIENT_ONCE",
  fixedAnnual: "FIXED_PER_YEAR",
};

/** The page quoted a symbol, not a code; these are the ones it was used with. */
const SYMBOL_TO_CODE: Record<string, string> = {
  $: "USD",
  "€": "EUR",
  "£": "GBP",
  "¥": "JPY",
  "₹": "INR",
  Rf: "MVR",
  ".ރ": "MVR",
};

/** The page's own constant: every client was committed for two years. */
const LEGACY_COMMITMENT_YEARS = 2;

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function toNumber(value: unknown, fallback: number): number {
  const parsed = typeof value === "string" ? Number(value.trim()) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : fallback;
}

function currencyCodeFor(value: unknown): string {
  if (typeof value !== "string") return "USD";
  const trimmed = value.trim();
  if (/^[A-Za-z]{3}$/.test(trimmed)) return trimmed.toUpperCase();
  return SYMBOL_TO_CODE[trimmed] ?? "USD";
}

/**
 * Recognise a legacy document. The schema stamp is the reliable signal; the key
 * shape is the fallback for a file saved out of `localStorage` by hand, which
 * carries no stamp at all.
 */
export function isLegacyScenarioDocument(value: unknown): boolean {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const source = value as Record<string, unknown>;
  if (source.schema === LEGACY_EXPORT_SCHEMA) return true;
  // "packages" alone is ambiguous — both formats have one — so require a field
  // only the old model ever had.
  return (
    "centralServerCost" in source ||
    "initialDevCost" in source ||
    "annualDevCost" in source ||
    "reuseHardware" in source ||
    "addons" in source
  );
}

export interface LegacyConversionOptions {
  /** Overrides the symbol in the document, when the product fixes a currency. */
  currencyCode?: string;
}

export function fromLegacyScenario(
  raw: unknown,
  options: LegacyConversionOptions = {},
): ScenarioModel {
  const source = asRecord(raw);
  const legacyPackages = asRecord(source.packages);
  const legacyAddons = asRecord(source.addons);

  const packages = LEGACY_PACKAGES.map(({ key, name }, index) => {
    const pkg = asRecord(legacyPackages[key]);
    return {
      id: key,
      name,
      sortOrder: index,
      colorIndex: index,
      billingModel: "SUBSCRIPTION",
      // The old page quoted one annual figure and had no per-client running cost.
      recurringPrice: toNumber(pkg.annualPrice, 0),
      recurringCost: 0,
      recurringPeriod: "ANNUAL",
      oneTimePrice: toNumber(pkg.setupFee, 0),
      oneTimeCost: toNumber(pkg.setupCost, 0),
      assetCost: toNumber(pkg.hardwareCost, 0),
      maintenanceMode: "NONE",
      maintenanceRatePct: 0,
      maintenanceFlatAmount: 0,
      maintenanceStartYear: 2,
      maintenanceAttachPct: 100,
    };
  });

  // `maintenanceStartsYear` gated only the annual development cost.
  const devStartYear = toNumber(source.maintenanceStartsYear, 1) === 2 ? 2 : 1;

  const lineItems: Partial<LineItemInput>[] = [
    {
      id: "central-server",
      name: "Central server",
      direction: "COST",
      group: "INFRASTRUCTURE",
      basis: "FIXED_ONE_TIME",
      amount: toNumber(source.centralServerCost, 0),
      startYear: 1,
    },
    {
      id: "initial-development",
      name: "Initial development",
      direction: "COST",
      group: "BUILD",
      basis: "FIXED_ONE_TIME",
      amount: toNumber(source.initialDevCost, 0),
      startYear: 1,
    },
    {
      id: "development-maintenance",
      name: "Development & maintenance",
      direction: "COST",
      group: "BUILD",
      basis: "FIXED_PER_YEAR",
      amount: toNumber(source.annualDevCost, 0),
      startYear: devStartYear,
    },
  ];

  for (const [key, name] of [
    ["bulkSms", "Bulk SMS"],
    ["pulseSim", "Pulse SIM"],
  ] as const) {
    const addon = asRecord(legacyAddons[key]);
    const basis = typeof addon.basis === "string" ? LEGACY_ADDON_BASIS[addon.basis] : undefined;
    lineItems.push({
      id: key,
      name,
      direction: "COST",
      group: "ADDON",
      basis: basis ?? "FIXED_PER_YEAR",
      amount: toNumber(addon.amount, 0),
      startYear: 1,
    });
  }

  const years = (Array.isArray(source.years) ? source.years : []).map((entry) => {
    const year = asRecord(entry);
    const legacyMix = asRecord(year.mix);
    const mix: Record<string, number> = {};
    for (const { key } of LEGACY_PACKAGES) mix[key] = toNumber(legacyMix[key], 0);
    return {
      newClients: toNumber(year.newClients, 0),
      mix,
      churnMode: "COUNT",
      churnCount: toNumber(year.churn, 0),
      churnRatePct: 0,
    };
  });

  return normalizeScenarioModel({
    currencyCode: options.currencyCode ?? currencyCodeFor(source.currency),
    horizonYears: years.length,
    commitmentYears: LEGACY_COMMITMENT_YEARS,
    discountRatePct: 0,
    reuseAssets: source.reuseHardware,
    packages,
    lineItems,
    years,
  });
}
